import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const residentialServices = [
    { name: "Deep Cleaning", href: "/services/deep-cleaning" },
    { name: "Regular House Cleaning", href: "/services/regular-cleaning" },
    { name: "Move-In Cleaning", href: "/services/move-in-cleaning" },
    { name: "Move-Out Cleaning", href: "/services/move-out-cleaning" },
    { name: "One-Time Cleaning", href: "/services/one-time-cleaning" },
    { name: "Airbnb Cleaning", href: "/services/airbnb-cleaning" },
    { name: "Apartment & Condo Cleaning", href: "/services/apartment-cleaning" },
    { name: "Holiday Cleaning", href: "/services/holiday-cleaning" },
    { name: "Spring Cleaning", href: "/services/spring-cleaning" },
    { name: "Post-Construction Cleaning", href: "/services/post-construction-cleaning" },
    { name: "Post-Renovation Cleaning", href: "/services/post-renovation-cleaning" },
    { name: "Window Cleaning", href: "/services/window-cleaning" },
  ];

  const commercialServices = [
    { name: "Office Cleaning", href: "/services/office-cleaning" },
    { name: "Commercial Cleaning", href: "/services/commercial-cleaning" },
  ];

  const moreItems = [
    { name: "Service Comparison", href: "/services/comparison" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms & Conditions", href: "/terms-and-conditions" },
  ];

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/">
              <img 
                src="/lovable-uploads/3755e2b1-d87a-4487-8a63-40cc6c8983ba.png" 
                alt="Routine Cleaners" 
                width={116}
                height={48}
                loading="eager"
                decoding="async"
                sizes="116px"
                className="h-10 w-auto lg:h-12"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="inline-flex items-center h-10 leading-none text-foreground hover:text-accent transition-colors font-medium"
              >
                {item.name}
              </a>
            ))}
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center h-10 leading-none text-foreground hover:text-accent transition-colors font-medium">
                Services
                <ChevronDown className="h-4 w-4 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 p-2">
                <div className="font-semibold text-accent text-sm px-2 py-1 mb-2">
                  Residential Services
                </div>
                {residentialServices.map((service) => (
                  <DropdownMenuItem key={service.name} asChild>
                    <a href={service.href} className="cursor-pointer">
                      {service.name}
                    </a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="my-2" />
                <div className="font-semibold text-accent text-sm px-2 py-1 mb-2">
                  Commercial Services
                </div>
                {commercialServices.map((service) => (
                  <DropdownMenuItem key={service.name} asChild>
                    <a href={service.href} className="cursor-pointer">
                      {service.name}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center h-10 leading-none text-foreground hover:text-accent transition-colors font-medium">
                More
                <ChevronDown className="h-4 w-4 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 p-2">
                {moreItems.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <a href={item.href} className="cursor-pointer">
                      {item.name}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="outline-blue" size="lg" asChild>
              <a href="tel:+14752517156" className="flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </a>
            </Button>
            <Button variant="hero" size="lg" asChild>
              <a href="/estimate">Get Free Estimate</a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-background border-b border-border">
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
              <div className="px-4 py-2 space-y-1">
                <a
                  href="/"
                  className="block px-3 py-2 text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </a>

                <a
                  href="/about"
                  className="block px-3 py-2 text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </a>

                <a
                  href="/contact"
                  className="block px-3 py-2 text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </a>
                
                {/* Mobile Services Dropdown */}
                <div>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="flex items-center justify-between w-full px-3 py-2 text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors"
                  >
                    Services
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="ml-4 space-y-1 border-l-2 border-muted pl-4 mt-2">
                      <div className="text-sm font-semibold text-muted-foreground px-3 py-1">Residential Services</div>
                      <a href="/services/regular-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Regular House Cleaning</a>
                      <a href="/services/deep-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Deep Cleaning</a>
                      <a href="/services/move-in-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Move-In Cleaning</a>
                      <a href="/services/move-out-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Move-Out Cleaning</a>
                      <a href="/services/one-time-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>One-Time Cleaning</a>
                      <a href="/services/spring-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Spring Cleaning</a>
                      <a href="/services/holiday-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Holiday Cleaning</a>
                      <a href="/services/post-renovation-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Post-Renovation</a>
                      <a href="/services/post-construction-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Post-Construction</a>
                      <a href="/services/airbnb-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Airbnb Cleaning</a>
                      
                      <div className="text-sm font-semibold text-muted-foreground px-3 py-1 mt-3">Commercial Services</div>
                      <a href="/services/office-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Office Cleaning</a>
                      <a href="/services/commercial-cleaning" className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" onClick={() => setIsMenuOpen(false)}>Commercial Cleaning</a>
                    </div>
                  )}
                </div>

                {/* Mobile More Dropdown */}
                <div>
                  <button
                    onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
                    className="flex items-center justify-between w-full px-3 py-2 text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors"
                  >
                    More
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileMoreOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileMoreOpen && (
                    <div className="ml-4 space-y-1 border-l-2 border-muted pl-4 mt-2">
                      {moreItems.map((item) => (
                        <a 
                          key={item.name}
                          href={item.href} 
                          className="block px-3 py-1 text-sm text-foreground hover:bg-accent/10 hover:text-accent rounded-md transition-colors" 
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="px-4 pb-4 pt-2 space-y-2 bg-background border-t border-border">
              <a
                href="tel:+14752517156"
                className="block w-full text-center px-4 py-2 bg-secondary text-secondary-foreground rounded-md touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Call Us
              </a>
              <a
                href="/estimate"
                className="block w-full text-center px-4 py-2 bg-primary text-primary-foreground rounded-md touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Get Free Estimate
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;